import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Paper,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import { toast, ToastContainer } from 'react-toastify';
import apiService from '../../services/apiService';
import SettingsSidebar from '../../components/Common/SettingsSidebar';

const AddModule: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    icon: '',
    route: '',
    is_active: true,
    sort_order: 0,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user makes selection
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Module name is required';
    } else if (!/^[a-z_]+$/.test(formData.name)) {
      newErrors.name = 'Module name must contain only lowercase letters and underscores';
    }

    if (!formData.display_name.trim()) {
      newErrors.display_name = 'Display name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.route.trim()) {
      newErrors.route = 'Route is required';
    } else if (!formData.route.startsWith('/')) {
      newErrors.route = 'Route must start with /';
    }

    if (formData.sort_order < 0) {
      newErrors.sort_order = 'Sort order must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      const moduleData = {
        name: formData.name,
        display_name: formData.display_name,
        description: formData.description,
        icon: formData.icon,
        route: formData.route,
        is_active: formData.is_active,
        sort_order: parseInt(formData.sort_order.toString()),
      };

      await apiService.createModule(moduleData);
      
      toast.success('Module created successfully!');
      navigate('/modules');
    } catch (error: any) {
      console.error('Error creating module:', error);
      
      if (error.response?.data?.errors) {
        const serverErrors = error.response.data.errors;
        const newErrors: Record<string, string> = {};
        
        Object.keys(serverErrors).forEach(key => {
          newErrors[key] = serverErrors[key][0];
        });
        
        setErrors(newErrors);
      } else {
        toast.error(error.message || 'Failed to create module');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/modules');
  };

  return (
    <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3 }}>
      <ToastContainer />
      
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, fontSize: '2rem' }}>
          Settings
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleCancel}
          sx={{
            borderRadius: '8px',
            borderColor: '#d1d5db',
            color: '#374151',
            '&:hover': {
              borderColor: '#9ca3af',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          Back to Modules
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Left Sidebar */}
        <Grid size={{ xs: 12, md: 3 }}>
          <SettingsSidebar />
        </Grid>

        {/* Main Content */}
        <Grid size={{ xs: 12, md: 9 }}>
          <Card sx={{ height: 'calc(100vh - 140px)', borderRadius: '12px' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 0 }}>
              {/* Form Header */}
              <Box sx={{ 
                p: 3, 
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#374151' }}>
                  Create New Module
                </Typography>
              </Box>

              {/* Form Content */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    {/* Basic Information */}
                    <Grid size={{ xs: 12 }}>
                      <Paper sx={{ p: 3, backgroundColor: '#f9fafb' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
                          Basic Information
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              fullWidth
                              name="name"
                              label="Module Name"
                              value={formData.name}
                              onChange={handleInputChange}
                              error={!!errors.name}
                              helperText={errors.name}
                              placeholder="e.g., user_management"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                },
                              }}
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              fullWidth
                              name="display_name"
                              label="Display Name"
                              value={formData.display_name}
                              onChange={handleInputChange}
                              error={!!errors.display_name}
                              helperText={errors.display_name}
                              placeholder="e.g., User Management"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                },
                              }}
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              fullWidth
                              name="description"
                              label="Description"
                              multiline
                              rows={3}
                              value={formData.description}
                              onChange={handleInputChange}
                              error={!!errors.description}
                              helperText={errors.description}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>

                    {/* Configuration */}
                    <Grid size={{ xs: 12 }}>
                      <Paper sx={{ p: 3, backgroundColor: '#f9fafb' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
                          Configuration
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              fullWidth
                              name="icon"
                              label="Icon"
                              value={formData.icon}
                              onChange={handleInputChange}
                              placeholder="e.g., people"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                },
                              }}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              fullWidth
                              name="route"
                              label="Route"
                              value={formData.route}
                              onChange={handleInputChange}
                              error={!!errors.route}
                              helperText={errors.route}
                              placeholder="e.g., /users"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                },
                              }}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              fullWidth
                              name="sort_order"
                              label="Sort Order"
                              type="number"
                              value={formData.sort_order}
                              onChange={handleInputChange}
                              error={!!errors.sort_order}
                              helperText={errors.sort_order}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '8px',
                                },
                              }}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                              <Typography variant="body2" sx={{ color: '#374151' }}>
                                Status:
                              </Typography>
                              <Box sx={{ 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                gap: 0.5,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '12px',
                                backgroundColor: formData.is_active ? '#dcfce7' : '#fef2f2',
                                color: formData.is_active ? '#166534' : '#991b1b',
                                fontSize: '0.75rem',
                                fontWeight: 500
                              }}>
                                <Box sx={{ 
                                  width: 6, 
                                  height: 6, 
                                  borderRadius: '50%', 
                                  backgroundColor: formData.is_active ? '#10b981' : '#ef4444'
                                }} />
                                {formData.is_active ? 'Active' : 'Inactive'}
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  </Grid>
                </form>
              </Box>

              {/* Form Footer */}
              <Box sx={{ 
                mt: 4, 
                pt: 3, 
                borderTop: '1px solid #e5e7eb',
                backgroundColor: '#f8fafc',
                borderRadius: '0 0 12px 12px',
                margin: '0 -24px -24px -24px',
                padding: '24px'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    onClick={handleSubmit}
                    sx={{
                      px: 4,
                      py: 2,
                      borderRadius: '8px',
                      backgroundColor: '#4A90E2',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '1rem',
                      textTransform: 'none',
                      minWidth: '140px',
                      '&:hover': {
                        backgroundColor: '#3b82f6',
                        boxShadow: '0 4px 12px rgba(74, 144, 226, 0.3)',
                      },
                      '&:disabled': {
                        backgroundColor: '#9ca3af',
                        color: 'white',
                      },
                    }}
                  >
                    {loading ? 'Creating...' : 'Create Module'}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddModule;
